/* eslint-disable sonarjs/no-duplicate-string */
import { action, makeObservable } from 'mobx';
import Cookies from 'js-cookie';
import { confirmAlert } from 'react-confirm-alert';
import debounce from 'lodash/debounce';
import React from 'react';
import i18n from 'i18next';
import isEqual from 'lodash/isEqual';
import dayjs from 'dayjs';
import config from '../../config/config';
import socketIo from '../socketIo';
import * as utils from '../../helpers/utils';
import CustomConfirmAlert from '../../components/Connect/Main/CustomConfirmAlert/CustomConfirmAlert';
import { getContactFullName, parseIncomingContacts } from '../../helpers/contactHelpers';
import {
  getLastMessageFromMessages,
  getLastUnseenMessageFromMessages,
  readByContact,
} from '../../helpers/messageHelpers';
import { BrowserDetect } from '../../helpers/browserDetect';
import { setBrowserAndCpTabBadges } from '../../helpers/notificationsHelper';

class ReceivingEvents {
  constructor(rootStore) {
    makeObservable(this);
    this.rootStore = rootStore;
  }

  @action updateLocations = (
    hasUnseenMessages,
    locationId,
    clearNotificationsFromForUser = null
  ) => {
    const { commonStore } = this.rootStore;
    commonStore.locations = commonStore.locations.map((m) => {
      if (m.CPLocationID.toString() === locationId.toString()) {
        return !clearNotificationsFromForUser
          ? { ...m, hasUnseenMessages }
          : { ...m, hasUnseenMessages, clearNotificationsFromForUser };
      }

      return m;
    });
  };

  playNotificationSound = () => {
    const { commonStore, settingsStore } = this.rootStore;
    const isOnMacAndFirefox = BrowserDetect.OS === 'Mac' && BrowserDetect.browser === 'Firefox';

    if (
      !settingsStore.getLoggedUserSettings.MuteSounds &&
      commonStore.audioContext &&
      commonStore.notificationsAudioRef &&
      !isOnMacAndFirefox
    ) {
      commonStore.audioContext
        .resume()
        .then(() => {
          commonStore.notificationsAudioRef
            .play()
            .then()
            .catch(() => {});
        })
        .catch(() => {});
    }
  };

  Notify = debounce(
    (valuesToUpdate) => {
      const { commonStore, settingsStore, sendingEvents } = this.rootStore;

      const lastMessage = getLastUnseenMessageFromMessages(
        valuesToUpdate,
        commonStore.getLoggedUser
      );

      const timeGate = dayjs().subtract(10, 'minutes');

      if (
        lastMessage?.Channel &&
        lastMessage?.Timestamp &&
        !lastMessage.Channel.IsDeleted &&
        new Date(lastMessage?.Timestamp) > timeGate
      ) {
        const senderIsNotFromShop =
          lastMessage?.Sender?.ContactType &&
          (lastMessage.Sender.ContactType.TypeId === utils.ContactTypeEnum.Customer.TypeId ||
            lastMessage.Sender.ContactType.TypeId === utils.ContactTypeEnum.WebChatContact.TypeId);

        const lastMessageIsNotAutoReplyOrSystem =
          lastMessage?.MessageType.TypeId !== utils.MessageTypeEnum.System.TypeId &&
          lastMessage?.MessageType.TypeId !== utils.MessageTypeEnum.AutoReply.TypeId;

        const showOnlyNewerMessages =
          !commonStore.lastNotificationMessage ||
          (commonStore.lastNotificationMessage &&
            lastMessageIsNotAutoReplyOrSystem &&
            lastMessage._id !== commonStore.lastNotificationMessage._id &&
            new Date(lastMessage.Timestamp) >
              new Date(commonStore.lastNotificationMessage.Timestamp));

        const loggedUserIsNotSnoozing =
          settingsStore?.getLoggedUserSettings &&
          (!settingsStore.getLoggedUserSettings.SnoozeNotificationsUntil ||
            new Date() >= new Date(settingsStore.getLoggedUserSettings.SnoozeNotificationsUntil));

        const loggedUserNotInMarkedAsUnreadBy = !lastMessage.MarkedAsUnreadBy.includes(
          commonStore.getLoggedUser._id
        );

        if (
          senderIsNotFromShop &&
          showOnlyNewerMessages &&
          loggedUserIsNotSnoozing &&
          loggedUserNotInMarkedAsUnreadBy
        ) {
          commonStore.setLastNotificationMessage(lastMessage);

          const senderFullName = getContactFullName(
            lastMessage.Sender,
            lastMessage.Channel?.CustomerPhoneNumber
          );

          if (!commonStore.ignoreNotifications) {
            const notificationTitle = senderFullName
              ? i18n.t('Common.NewMessageFromContact', { contactName: senderFullName })
              : i18n.t('Common.NewMessage');

            commonStore.setNotificationData(
              notificationTitle,
              {
                tag: Date.now(),
                body: lastMessage.FilePath ? 'Sent file' : lastMessage.MessageText,
                renotify: true,
                silent:
                  settingsStore.getLoggedUserSettings.MuteSounds || !!commonStore.audioContext,
              },
              lastMessage.Channel
            );
          }

          if (
            settingsStore.getLoggedUserSettings.PersistentNotifications &&
            (lastMessage?.Sender.ContactType.TypeId === utils.ContactTypeEnum.Customer.TypeId ||
              lastMessage?.Sender.ContactType.TypeId ===
                utils.ContactTypeEnum.WebChatContact.TypeId)
          ) {
            sendingEvents.socketEmitAddPersistentNotification(lastMessage);
          }

          this.playNotificationSound();
        }
      }
    },
    1000,
    { leading: true, trailing: false }
  );

  NotifyUnanswered = debounce(
    (channel, isInitial) => {
      const { commonStore, settingsStore } = this.rootStore;

      const loggedUserIsNotSnoozing =
        settingsStore?.getLoggedUserSettings &&
        (!settingsStore.getLoggedUserSettings.SnoozeNotificationsUntil ||
          new Date() >= new Date(settingsStore.getLoggedUserSettings.SnoozeNotificationsUntil));

      const contact = channel?.Contacts?.[0];

      const senderFullName = getContactFullName(contact, channel?.CustomerPhoneNumber);

      if (!commonStore.ignoreNotifications && loggedUserIsNotSnoozing) {
        const notificationTitle = senderFullName
          ? `${i18n.t('Common.WebsiteChat')} - ${senderFullName}`
          : `${i18n.t('Common.WebsiteChat')}`;

        commonStore.setNotificationData(
          notificationTitle,
          {
            tag: Date.now(),
            body: isInitial
              ? i18n.t('Common.NewWebChat')
              : i18n.t('Common.WarningUnansweredWebChat'),
            renotify: true,
            silent: settingsStore.getLoggedUserSettings.MuteSounds || !!commonStore.audioContext,
          },
          channel
        );
      }

      this.playNotificationSound();
    },
    1000,
    { leading: true, trailing: false }
  );

  loginToSelectedLocation = (selectedLocation) => {
    const { commonStore } = this.rootStore;

    commonStore.setSelectedLocation(selectedLocation);
    commonStore.setLoggedIn(true);
    commonStore.setConnectToLocationSocket();
  };

  AttachLoginSocketReceivingEvents = () => {
    const { commonStore } = this.rootStore;

    commonStore.loginToken = config.isDevelopmentEnv
      ? process.env.REACT_APP_loginToken || Cookies.get('kukuiconnecttokendev')
      : Cookies.get('kukuiconnecttoken');

    commonStore.clientLocationsFromCp =
      config.isDevelopmentEnv && window.location.href !== config.publicUrl
        ? process.env.REACT_APP_locationIds || Cookies.get('kukuiconnectlocationsdev')
        : Cookies.get('kukuiconnectlocations');

    socketIo.loginSocket.on('server:login', (result) => {
      socketIo.loginSocket.close();

      if (result && result.StatusCode === 200 && result.ResultObject) {
        const resultObject = JSON.parse(result.ResultObject);
        let currentLocations = result.Locations;

        if (currentLocations?.length) {
          if (commonStore.clientLocationsFromCp && commonStore.cpHeaderHeight) {
            commonStore.clientLocationsFromCp = commonStore.clientLocationsFromCp
              .toString()
              .split('|');

            const filteredLocations = result.Locations.filter((f) =>
              commonStore.clientLocationsFromCp.some(
                (s) => s.toString() === f.CPLocationID.toString()
              )
            );

            if (filteredLocations?.length) {
              currentLocations = filteredLocations;
            }
          }

          commonStore.setUsername(resultObject.Email);
          commonStore.setCpUserId(resultObject.UserID);
          commonStore.setLocations(
            currentLocations.sort((a, b) => a.CPLocationID - b.CPLocationID)
          );
          commonStore.setUserPreviousLoginInfo({
            ...resultObject,
            LastLoggedLocationId: result.LastLoggedLocationId,
          });

          const queryStringLocationId = utils.getQueryParameter('locationId');

          const locationToRedirect = currentLocations.find(
            (l) => l.CPLocationID.toString() === queryStringLocationId
          );

          if (queryStringLocationId && !locationToRedirect) {
            confirmAlert({
              customUI: ({ onClose }) => (
                <CustomConfirmAlert
                  title={i18n.t('LoginScreen.LocationNotFound')}
                  falseText="Close"
                  falseEvent={onClose}
                />
              ),
            });
          }

          if (locationToRedirect) {
            this.loginToSelectedLocation(locationToRedirect);
          } else if (currentLocations?.length === 1) {
            this.loginToSelectedLocation(currentLocations[0]);
          } else if (currentLocations?.length > 1) {
            commonStore.setSelectedLocation(
              commonStore.userPreviousLoginInfo?.LastLoggedLocationId
                ? result.Locations.find(
                    (f) =>
                      f.CPLocationID.toString() ===
                      commonStore.userPreviousLoginInfo.LastLoggedLocationId.toString()
                  )
                : currentLocations[0]
            );

            commonStore.setShowLocationPicker(true);
          }
        } else {
          confirmAlert({
            customUI: ({ onClose }) => (
              <CustomConfirmAlert
                title={i18n.t('LoginScreen.AuthenticationFailedNoLocations')}
                falseText="Close"
                falseEvent={onClose}
              />
            ),
          });
        }
      } else {
        const error = result ? result.ErrorsList[0] : null;

        if (error?.ErrorCode === 'CP_1001' || error?.ErrorCode === 'CP_1002') {
          error.ErrorMessage = i18n.t('LoginScreen.IncorrectEmailOrPassword');
        }

        if (!commonStore.loginToken) {
          confirmAlert({
            customUI: ({ onClose }) => (
              <CustomConfirmAlert
                title={
                  error?.ErrorMessage
                    ? `${i18n.t('LoginScreen.AuthenticationFailedShort')}: ${error.ErrorMessage}`
                    : i18n.t('LoginScreen.AuthenticationFailed')
                }
                falseText="Close"
                falseEvent={onClose}
              />
            ),
          });
        }

        commonStore.loginToken = null;
      }

      commonStore.setPassword('');
      commonStore.setAuthenticationLoading(false);
    });
  };

  AttachSocketReceivingEvents = () => {
    const {
      commonStore,
      settingsStore,
      autoReplyStore,
      autoReplyV2Store,
      messageStore,
      contactStore,
      errorLogStore,
      pushNotificationTokenStore,
      contactTypeStore,
      channelStore,
      smsDeliveryStatusStore,
      phoneNumberStore,
      messageTypeStore,
      channelTypeStore,
      emailStore,
      cannedMessageStore,
      workTimeStore,
      sendingEvents,
    } = this.rootStore;

    socketIo.locationSocket.on('connect', () => {
      const initialDataToSend = {
        username: commonStore.username,
        userRole: 'Admin',
        locations: commonStore.locations,
        userId: commonStore.userPreviousLoginInfo.UserID,
        firstName: commonStore.userPreviousLoginInfo.FirstName,
        lastName: commonStore.userPreviousLoginInfo.LastName,
        email: commonStore.username,
        numberOfMessagesToShow: utils.maxNumberOfItemsByContainerSize(
          document,
          commonStore.messagesContainerRef
        ),
        numberOfChannelsToShow: utils.maxNumberOfItemsByContainerSize(
          document,
          commonStore.leftSideBarListContainerRef,
          61
        ),
      };

      sendingEvents.socketEmitLoadInitialData(initialDataToSend);

      if (commonStore.isDisconnected) {
        commonStore.setIsDisconnected(false);
        commonStore.setReconnectAttemptCount(0);
      }
    });

    socketIo.locationSocket.on('server:loadInitialData', (incomingData) => {
      if (!incomingData || !incomingData?.settings) {
        confirmAlert({
          customUI: ({ onClose }) => (
            <CustomConfirmAlert
              title="Couldn't connect to the server, please try again later"
              falseText="Close"
              falseEvent={onClose}
            />
          ),
        });

        commonStore.setLogoutStatus();

        return;
      }

      const mappedData = utils.mapData(incomingData);
      commonStore.setInitialData(mappedData);

      commonStore.setMaxVisibleMessages(
        mappedData.messages.filter((f) =>
          mappedData.settings.SelectedChannel
            ? f.Channel._id === mappedData.settings.SelectedChannel._id
            : null
        ).length
      );

      commonStore.setLastNotificationMessage(getLastMessageFromMessages(mappedData.messages));

      setBrowserAndCpTabBadges(
        commonStore.locations.some((s) => s.hasUnseenMessages) ||
          !!messageStore.computedGetUnseenMessages(null, null, null).length,
        commonStore.favicon
      );
    });

    socketIo.locationSocket.on('disconnect', () => {
      commonStore.setIsDisconnected(true);
      commonStore.setFaviconBadgeCount(0);
    });

    socketIo.locationSocket.on('reconnect_attempt', (attempt) => {
      commonStore.setReconnectAttemptCount(attempt);

      if (attempt > utils.reconnectAttemptNumber) {
        commonStore.setLogoutStatus();
        confirmAlert({
          customUI: ({ onClose }) => (
            <CustomConfirmAlert
              title="Server is offline, please try again later"
              falseText="Close"
              falseEvent={onClose}
            />
          ),
        });
      }
    });

    socketIo.locationSocket.on('server:message', (result) => {
      commonStore.setDragAndDropChildElementCounter(0);
      commonStore.setIsUploading(false);

      if (!result) {
        confirmAlert({
          customUI: ({ onClose }) => (
            <CustomConfirmAlert
              title="Upload failed. Please try again."
              falseText="Close"
              falseEvent={onClose}
            />
          ),
        });
      } else {
        commonStore.setMaxVisibleMessages(commonStore.maxVisibleMessages + 1);
      }
    });

    socketIo.locationSocket.on('server:room', (messagesResult) => {
      if (messagesResult) {
        const result = {
          messages: messagesResult.messages,
          lastSeenMessageForChannel: messagesResult.lastSeenMessageForChannel
            ? messagesResult.lastSeenMessageForChannel
            : {},
          channel: messagesResult.channel,
        };

        commonStore.setChannelInfoInnerComponent(null);

        commonStore.setOldScrollHeight(0);

        commonStore.setMaxVisibleMessages(
          result.messages.length !== 0 ? result.messages.length : 1
        );

        messageStore.setMessages(
          messageStore.messages.filter(
            (f) =>
              !f.SeenBy.some((s) => s._id === commonStore.getLoggedUser._id) ||
              (f.Channel.LastMessage && f._id === f.Channel.LastMessage._id)
          )
        );

        settingsStore.setSelectedChannel(channelStore.UpsertAndGet(result.channel));

        commonStore.setChannelHasMoreMessages(
          result.messages.length >=
            utils.maxNumberOfItemsByContainerSize(document, commonStore.messagesContainerRef)
        );

        commonStore.setLastSeenMessageForChannel(result.lastSeenMessageForChannel);
        commonStore.scrollToSelectedChannel();

        messageStore.UpsertAndGet(result.messages);
      }

      commonStore.clearChannelNotifications();
      commonStore.setShowMessageLoadScreen(false);
    });

    socketIo.locationSocket.on('server:loadMoreMessages', (incomingData) => {
      if (incomingData) {
        const incMessages = incomingData.moreMessages;
        commonStore.setMaxVisibleMessages(commonStore.maxVisibleMessages + incMessages.length);
        messageStore.UpsertAndGet(incMessages);

        if (incomingData.lastBatch) {
          commonStore.setChannelHasMoreMessages(false);
        }
      }
    });

    socketIo.locationSocket.on('server:loadMoreChannels', (incomingData) => {
      if (incomingData) {
        messageStore.UpsertAndGet(incomingData.messages);
        if (incomingData.lastBatch) {
          commonStore.setLocationHasMoreChannels(false);
        }
      }

      commonStore.setIsWaitingForLoadMoreChannels(false);
    });

    socketIo.locationSocket.on('server:transferWebChatToSMS', (incomingData) => {
      if (incomingData?.CustomerPhoneNumber && incomingData?.ChannelType) {
        sendingEvents.socketEmitRoom(
          incomingData,
          utils.maxNumberOfItemsByContainerSize(document, commonStore.messagesContainerRef)
        );
      } else {
        commonStore.setSnackbarMessage(i18n.t('ChatInput.FailedToTransferWebChatToSMS'));
      }
    });

    socketIo.locationSocket.on('server:toggleAutoReplyIsActiveForTextChat', (incomingData) => {
      if (incomingData?.scheduleConflicts) {
        commonStore.setConflictingAutoReplies(incomingData);
      }
    });

    socketIo.locationSocket.on('server:DbListenerChanges', (incData) => {
      const receivedData =
        Array.isArray(incData) && incData.filter((f) => f && f.name !== null && f.value !== null);

      if (commonStore.isDataLoaded && receivedData.length) {
        const settings = settingsStore.getLoggedUserSettings;
        const valuesToUpdate = [];

        switch (receivedData[0].name) {
          case 'AutoReply':
            if (commonStore.ignoreAutoReplyEntries > 0) {
              commonStore.setIgnoreAutoReplyEntries(commonStore.ignoreAutoReplyEntries - 1);
            } else {
              for (let i = 0, len = receivedData.length; i < len; i += 1) {
                valuesToUpdate.push(receivedData[i].value); // eslint-disable-line no-param-reassign
              }

              autoReplyStore.UpsertAndGet(valuesToUpdate);
            }
            break;
          case 'AutoReplyV2':
            if (commonStore.ignoreAutoReplyEntries > 0) {
              commonStore.setIgnoreAutoReplyV2Entries(commonStore.ignoreAutoReplyEntries - 1);
            } else {
              for (let i = 0, len = receivedData.length; i < len; i += 1) {
                valuesToUpdate.push(receivedData[i].value); // eslint-disable-line no-param-reassign
              }

              autoReplyV2Store.UpsertAndGet(valuesToUpdate);
            }
            break;
          case 'Channel':
            for (let i = 0, len = receivedData.length; i < len; i += 1) {
              const currChannel = receivedData[i].value;

              valuesToUpdate.push(currChannel); // eslint-disable-line no-param-reassign
            }

            channelStore.UpsertAndGet(valuesToUpdate);
            break;
          case 'ChannelType':
            for (let i = 0, len = receivedData.length; i < len; i += 1) {
              valuesToUpdate.push(receivedData[i].value); // eslint-disable-line no-param-reassign
            }

            channelTypeStore.UpsertAndGet(valuesToUpdate);
            break;
          case 'Contact':
            for (let i = 0, len = receivedData.length; i < len; i += 1) {
              valuesToUpdate.push(receivedData[i].value); // eslint-disable-line no-param-reassign
            }

            contactStore.UpsertAndGet(valuesToUpdate);
            break;
          case 'ContactType':
            for (let i = 0, len = receivedData.length; i < len; i += 1) {
              valuesToUpdate.push(receivedData[i].value); // eslint-disable-line no-param-reassign
            }

            contactTypeStore.UpsertAndGet(valuesToUpdate);
            break;
          case 'Email':
            for (let i = 0, len = receivedData.length; i < len; i += 1) {
              valuesToUpdate.push(receivedData[i].value); // eslint-disable-line no-param-reassign
            }

            emailStore.UpsertAndGet(valuesToUpdate);
            break;
          case 'PushNotificationToken':
            for (let i = 0, len = receivedData.length; i < len; i += 1) {
              valuesToUpdate.push(receivedData[i].value); // eslint-disable-line no-param-reassign
            }

            pushNotificationTokenStore.UpsertAndGet(valuesToUpdate);
            break;
          case 'ErrorLog':
            for (let i = 0, len = receivedData.length; i < len; i += 1) {
              valuesToUpdate.push(receivedData[i].value); // eslint-disable-line no-param-reassign
            }

            errorLogStore.UpsertAndGet(valuesToUpdate);
            break;
          case 'Message':
            for (let i = 0, len = receivedData.length; i < len; i += 1) {
              const currMessage = receivedData[i].value;

              const hasChannel = currMessage.Channel;
              const hasMessageType = currMessage.MessageType;
              const isNotDeleted = !currMessage.IsDeleted;
              const isSMS = currMessage.MessageType.TypeId === utils.MessageTypeEnum.SMS.TypeId;
              const isDirectMessage =
                currMessage.MessageType.TypeId === utils.MessageTypeEnum.DirectMessage.TypeId;
              const isAutoreply =
                currMessage.MessageType.TypeId === utils.MessageTypeEnum.AutoReply.TypeId;
              const isSystemMessage =
                currMessage.MessageType.TypeId === utils.MessageTypeEnum.System.TypeId ||
                currMessage.MessageType.TypeId ===
                  utils.MessageTypeEnum.WelcomeChannelMessage.TypeId ||
                currMessage.MessageType.TypeId ===
                  utils.MessageTypeEnum.ClosedChannelMessage.TypeId;
              const smsDeliveryStatusIsNullOrSent =
                currMessage.SMSDeliveryStatus === null ||
                (currMessage.SMSDeliveryStatus &&
                  currMessage.SMSDeliveryStatus.TypeId === utils.SMSDeliveryStatusEnum.Sent.TypeId);
              const hasCurrentUserAsSender =
                currMessage.Sender && currMessage.Sender._id === commonStore.getLoggedUser._id;

              if (
                hasChannel &&
                hasMessageType &&
                isNotDeleted &&
                (((isSMS || isDirectMessage || isAutoreply || isSystemMessage) &&
                  smsDeliveryStatusIsNullOrSent) ||
                  hasCurrentUserAsSender)
              ) {
                valuesToUpdate.push(currMessage);
              }
            }

            if (valuesToUpdate?.length) {
              // eslint-disable-next-line no-case-declarations
              const messagesForChannel = valuesToUpdate.filter(
                (f) => settings.SelectedChannel?._id === f.Channel?._id
              );

              if (messagesForChannel?.length) {
                commonStore.setMaxVisibleMessages(
                  commonStore.maxVisibleMessages + messagesForChannel.length
                );

                if (
                  !messagesForChannel.some((f) =>
                    messageStore.messages.some((s) => s._id === f._id)
                  )
                ) {
                  commonStore.setOldScrollHeight(0);
                }
              }

              messageStore.UpsertAndGet(valuesToUpdate);

              this.Notify(valuesToUpdate);

              setBrowserAndCpTabBadges(
                commonStore.locations.some((s) => s.hasUnseenMessages) ||
                  !!messageStore.computedGetUnseenMessages(null, null, null).length,
                commonStore.favicon
              );
            }
            break;
          case 'MessageType':
            for (let i = 0, len = receivedData.length; i < len; i += 1) {
              valuesToUpdate.push(receivedData[i].value); // eslint-disable-line no-param-reassign
            }

            messageTypeStore.UpsertAndGet(valuesToUpdate);
            break;
          case 'PhoneNumber':
            for (let i = 0, len = receivedData.length; i < len; i += 1) {
              valuesToUpdate.push(receivedData[i].value); // eslint-disable-line no-param-reassign
            }

            phoneNumberStore.UpsertAndGet(valuesToUpdate);
            break;
          case 'SMSDeliveryStatus':
            for (let i = 0, len = receivedData.length; i < len; i += 1) {
              valuesToUpdate.push(receivedData[i].value); // eslint-disable-line no-param-reassign
            }

            smsDeliveryStatusStore.UpsertAndGet(valuesToUpdate);
            break;
          case 'Settings':
            for (let i = 0, len = receivedData.length; i < len; i += 1) {
              valuesToUpdate.push(receivedData[i].value); // eslint-disable-line no-param-reassign
            }

            if (
              valuesToUpdate &&
              commonStore.getLoggedUser &&
              valuesToUpdate.some((x) => x.User && x.User._id === commonStore.getLoggedUser._id)
            ) {
              if (commonStore.ignoreSettingsEntries > 0) {
                commonStore.setIgnoreSettingsEntries(commonStore.ignoreSettingsEntries - 1);
              } else {
                const changedUserSettings = valuesToUpdate.filter(
                  (x) => x.User && x.User._id === commonStore.getLoggedUser._id
                );

                const shouldReloadRoom =
                  settings?.ShowHiddenMessages !== changedUserSettings[0].ShowHiddenMessages;

                const shouldResetSelectedRoom =
                  changedUserSettings[0].HiddenChannels.includes(settings?.SelectedChannel?._id) &&
                  !settings?.HiddenChannels.includes(settings?.SelectedChannel?._id);

                const shouldReloadChannelsAndMessages =
                  settings?.FilterRecentMessagesFrom !==
                  changedUserSettings[0].FilterRecentMessagesFrom;

                settingsStore.UpsertAndGet(changedUserSettings);

                if (settings?.SelectedChannel) {
                  if (shouldReloadRoom) {
                    sendingEvents.socketEmitRoom(
                      settings.SelectedChannel,
                      utils.maxNumberOfItemsByContainerSize(
                        document,
                        commonStore.messagesContainerRef
                      )
                    );
                  } else if (shouldResetSelectedRoom) {
                    const [newSelectedChannel] = channelStore.getChannels.filter(
                      (f) => f._id !== settings.SelectedChannel._id
                    );
                    sendingEvents.socketEmitRoom(
                      newSelectedChannel,
                      utils.maxNumberOfItemsByContainerSize(
                        document,
                        commonStore.messagesContainerRef
                      )
                    );
                  }
                }

                if (shouldReloadChannelsAndMessages) {
                  this.rootStore.commonStore.reloadChannelsAndMessages();
                }
              }
            }
            break;
          case 'CannedMessage':
            for (let i = 0, len = receivedData.length; i < len; i += 1) {
              valuesToUpdate.push(receivedData[i].value); // eslint-disable-line no-param-reassign
            }

            cannedMessageStore.UpsertAndGet(valuesToUpdate);
            break;
          case 'WorkTime':
            for (let i = 0, len = receivedData.length; i < len; i += 1) {
              valuesToUpdate.push(receivedData[i].value); // eslint-disable-line no-param-reassign
            }

            workTimeStore.UpsertAndGet(valuesToUpdate);
            break;
          case 'CpDataSync':
            for (let i = 0, len = receivedData.length; i < len; i += 1) {
              valuesToUpdate.push(receivedData[i].value); // eslint-disable-line no-param-reassign
            }

            if (
              valuesToUpdate.some(
                (s) =>
                  s.Location.toString() === commonStore.selectedLocation.CPLocationID.toString() &&
                  s.Synced
              )
            ) {
              socketIo.locationSocket.close();
              socketIo.locationSocket.connect();
            }
            break;
          default:
            break;
        }
      }
    });

    // const parseIncomingContacts = (incContacts) => {
    //   if (!incContacts || !Array.isArray(incContacts) || !incContacts.length) {
    //     return [];
    //   }

    //   const settings = settingsStore.getLoggedUserSettings;

    //   return incContacts
    //     .reduce((allContacts, contact) => {
    //       if (
    //         contact.ContactType &&
    //         contact.ContactType.TypeId === utils.ContactTypeEnum.Customer.TypeId &&
    //         contact.PhoneNumbers.length
    //       ) {
    //         const filteredNumbers = contact.PhoneNumbers.filter(
    //           (f) =>
    //             f?.Number?.includes(this.rootStore.commonStore.channelSearchInput) ||
    //             f?.TwilioFormat?.includes(this.rootStore.commonStore.channelSearchInput)
    //         );

    //         const phoneNumbers =
    //           filteredNumbers.length === 0 ? contact.PhoneNumbers : filteredNumbers;

    //         for (let j = 0, numbersLen = phoneNumbers.length; j < numbersLen; j += 1) {
    //           const contactWithSingleNumber = {
    //             ...contact,
    //             PhoneNumbers: [phoneNumbers[j]],
    //           };
    //           allContacts.push(contactWithSingleNumber);
    //         }
    //       } else {
    //         allContacts.push(contact);
    //       }

    //       return allContacts;
    //     }, [])
    //     .map((contact, index) => {
    //       let label = '';

    //       if (contact.FirstName?.trim() || contact.LastName?.trim()) {
    //         label = getContactFullName(contact);
    //       } else if (contact.CpUsername?.trim()) {
    //         label = `${contact.CpUsername}`;
    //       } else if (contact.PhoneNumbers?.length) {
    //         label = `${contact.PhoneNumbers[0].Number}`;
    //       }

    //       return {
    //         label,
    //         value: label,
    //         data: contact,
    //         isChannel: false,
    //         selectedIndex: index,
    //         contactType: contact.ContactType ? contact.ContactType.TypeName : null,
    //         number: contact.PhoneNumbers.length ? `${contact.PhoneNumbers[0].Number}` : null,
    //         isSelected:
    //           settings.SelectedChannel &&
    //           settings.SelectedChannel.Contacts.some((cont) => cont._id === contact._id),
    //       };
    //     });
    // };

    socketIo.locationSocket.on('server:getSearchResult', (receivedData) => {
      if (receivedData && commonStore.getLoggedUser) {
        const incContacts = receivedData.filteredContacts;

        commonStore.setSearchResultData(
          parseIncomingContacts(
            incContacts,
            this.rootStore.commonStore.channelSearchInput,
            settingsStore.getLoggedUserSettings
          )
        );
      }

      commonStore.setIsFetchingSearchResults(false);
      commonStore.setIsLoadingSearchResultData(false);
    });

    socketIo.locationSocket.on('server:blockPhoneNumber', (result) => {
      commonStore.setSnackbarMessage(
        result ? i18n.t('Common.BlockPhoneNumber') : i18n.t('Common.FailedToBlockPhoneNumber')
      );
    });

    socketIo.locationSocket.on('server:unblockPhoneNumber', (result) => {
      commonStore.setSnackbarMessage(
        result ? i18n.t('Common.UnblockPhoneNumber') : i18n.t('Common.FailedToUnblockPhoneNumber')
      );
    });

    socketIo.locationSocket.on('server:unansweredWcChannel', (data) => {
      const { channel, isInitial } = data;
      this.NotifyUnanswered(channel, isInitial);
    });

    socketIo.locationSocket.on('server:matchContact', (result) => {
      commonStore.setContactInfoMatchedContacts(result);
    });

    socketIo.locationSocket.on('server:contactStartedTyping', (data) => {
      if (data?.contactId !== commonStore.loggedUserId) {
        commonStore.setAddContactTyping(data);
      }
    });

    socketIo.locationSocket.on('server:contactStoppedTyping', (data) => {
      if (data?.contactId !== commonStore.loggedUserId) {
        commonStore.setRemoveContactTyping(data);
      }
    });

    socketIo.locationSocket.on('server:toggleIsWebchatEnabled', (data) => {
      if (data !== null) {
        commonStore.setSnackbarMessage(
          data.status ? i18n.t('Common.WebsiteChatEnabled') : i18n.t('Common.WebsiteChatDisabled')
        );
        commonStore.setIsWebchatEnabled(data.status);
      } else {
        commonStore.setSnackbarMessage(i18n.t('Common.WebsiteChatFailedToUpdate'));
      }
    });
  };

  AttachOtherLocationsSocketEvents = (currentSocketConnection) => {
    const { commonStore, messageStore } = this.rootStore;

    currentSocketConnection.on('server:loadInitialUnseenMessagesIds', (incData) => {
      if (incData) {
        this.updateLocations(
          incData.hasUnseenMessages,
          currentSocketConnection?.nsp?.replace('/', ''),
          incData.clearNotificationsFromForUser
        );

        setBrowserAndCpTabBadges(
          commonStore.locations.some((s) => s.hasUnseenMessages) ||
            !!messageStore.computedGetUnseenMessages(null, null, null).length,
          commonStore.favicon
        );
      }
    });

    currentSocketConnection.on('server:DbListenerChanges', (incData) => {
      const receivedData =
        Array.isArray(incData) && incData.filter((f) => f && f.name !== null && f.value !== null);

      if (
        receivedData?.length &&
        receivedData[0].name === 'Message' &&
        commonStore?.getLoggedUser?._id
      ) {
        for (let i = 0, len = receivedData.length; i < len; i += 1) {
          const currMessage = receivedData[i].value;
          const isSms =
            currMessage.MessageType &&
            currMessage.MessageType.TypeId === utils.MessageTypeEnum.SMS.TypeId;
          const isSent =
            currMessage.SMSDeliveryStatus === null ||
            (currMessage.SMSDeliveryStatus &&
              currMessage.SMSDeliveryStatus.TypeId === utils.SMSDeliveryStatusEnum.Sent.TypeId);

          if (isSms && isSent) {
            const locationId = currentSocketConnection?.nsp?.replace('/', '');

            const isSeenByUser = readByContact(
              currMessage,
              commonStore.getLoggedUser._id,
              commonStore.locations.find((f) => f.CPLocationID.toString() === locationId.toString())
            );

            if (isSeenByUser) {
              this.updateLocations(false, locationId);
            } else {
              this.updateLocations(true, locationId);

              // Support users ignore notifications from other locations, when not in the CP, to avoid spam
              if (
                commonStore.getLoggedUser.ContactType.TypeId !==
                  utils.ContactTypeEnum.Support.TypeId ||
                (commonStore.getLoggedUser.ContactType.TypeId ===
                  utils.ContactTypeEnum.Support.TypeId &&
                  commonStore.cpHeaderHeight)
              ) {
                this.Notify([currMessage]);
              }
            }

            setBrowserAndCpTabBadges(
              commonStore.locations.some((s) => s.hasUnseenMessages) ||
                !!messageStore.computedGetUnseenMessages(null, null, null).length,
              commonStore.favicon
            );
          }
        }
      }
    });

    currentSocketConnection.on('connect', () => {
      currentSocketConnection.emit('client:loadInitialUnseenMessagesIds');
    });
  };

  AttachCommonSocketEvents = (currentSocketConnection) => {
    currentSocketConnection.on('server:DbListenerChanges', (incData) => {
      const receivedData =
        Array.isArray(incData) && incData.filter((f) => f && f.name !== null && f.value !== null);

      if (receivedData?.length) {
        if (receivedData[0].name === 'FeatureFlag') {
          this.rootStore.featureFlagStore.UpsertAndGet(incData[0].value);
        } else if (receivedData[0].name === 'Location') {
          for (let i = 0, len = receivedData.length; i < len; i += 1) {
            const existingLocation = this.rootStore.commonStore.locations.find(
              (x) => x.CPLocationID.toString() === receivedData[i].value.LocationId.toString()
            );

            if (existingLocation) {
              existingLocation.WebsiteChatIsActive = receivedData[i].value.WebsiteChatIsActive;

              if (
                !this.rootStore.commonStore?.getTransferLocationsList?.length &&
                this.rootStore.commonStore?.transferLocationListOpen
              ) {
                this.rootStore.commonStore.setTransferLocationListOpen(false);
              }

              if (
                receivedData[i].value.LocationId.toString() ===
                this.rootStore.commonStore.selectedLocation.CPLocationID.toString()
              ) {
                this.rootStore.phoneNumberStore.UpsertAndGet(
                  receivedData[i].value.SelectedTrackingPhoneNumber
                );
                this.rootStore.commonStore.setLocationTrackingNumber(
                  receivedData[i].value.SelectedTrackingPhoneNumber
                );
                this.rootStore.commonStore.setIsWebchatEnabled(
                  receivedData[i].value.IsWebchatEnabled
                );
                this.rootStore.commonStore.setClientWebsiteUrl(receivedData[i].value.WebsiteUrl);
                const availableModes = utils.getAvailableModesForLocation(receivedData[i].value);
                if (!isEqual(availableModes, this.rootStore.commonStore.availableModes)) {
                  socketIo.locationSocket.close();
                  socketIo.locationSocket.connect();
                  this.rootStore.commonStore.setTransferLocationListOpen(false);
                }
              }
            }
          }
        }
      }
    });
  };
}

export default ReceivingEvents;
